import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { memo } from "react";

interface MultiplierSlotProps {
  value: string;
  color: string;
  isWinning?: boolean;
  isActive?: boolean;
}

const SlotIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== "slotColor" && prop !== "isActive",
})<{ slotColor: string; isActive?: boolean }>(
  ({ slotColor, isActive, theme }) => ({
    width: "clamp(20px, 5vw, 40px)", // ✅ dinamično skaliranje
    height: "clamp(4px, 0.8vw, 6px)", // ✅ skaluje i visinu
    borderRadius: 4,
    backgroundColor: slotColor,
    boxShadow: isActive
      ? `0 0 12px ${slotColor}80, 0 0 20px ${slotColor}40`
      : "none",
    transition: "all 0.3s ease",
    position: "relative",
    transform: "translateZ(0)",
    willChange: "transform, box-shadow",

    [theme.breakpoints.down("sm")]: {
      width: "clamp(14px, 4vw, 30px)",
      height: "clamp(3px, 0.6vw, 5px)",
    },
  })
);

export const MultiplierSlot = memo(
  ({
    value,
    color,
    isWinning = false,
    isActive = false,
  }: MultiplierSlotProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isSmallMobile = useMediaQuery("(max-width:1000px)");
    const isSmallIpad = useMediaQuery(
      "(min-width: 600px) and (max-width: 930px)"
    );
    const isLargeIpad = useMediaQuery(
      "(min-width: 931px) and (max-width: 1200px)"
    );

    const getFontSize = () => {
      if (isSmallMobile) return "14px";
      if (isMobile) return "15px";
      return "16px";
    };

    const getPadding = () => {
      if (isSmallMobile) return "3px 11px";
      if (isMobile) return "4px 8px";
      return "6px 8px";
    };

    const getMarginTop = () => {
      if (isSmallMobile) return "-4px";
      if (isMobile) return "-10px";

      return "-12px";
    };

    return (
      <Stack
        spacing={isSmallMobile ? 0.8 : isMobile ? 1 : 1.5}
        alignItems="center"
        sx={{
          position: "relative",

          padding: getPadding(),
          borderRadius: "8px",
          left: {
            xs: "25px", // 📱 mobile
            sm: "20px", // 📲 tablet
            md: "10px", // 💻 desktop
          },

          background: isActive
            ? `linear-gradient(135deg, ${color}15 0%, transparent 70%)`
            : "transparent",
          border: isActive ? `1px solid ${color}20` : "1px solid transparent",
          transition: "all 0.3s ease",
          backdropFilter: isActive ? "blur(8px)" : "none",
          transform: isSmallIpad
            ? "translate(-5px, 0px)" // 📱 iPad manji → udesno 80px i dole 30px
            : isLargeIpad
            ? "translate(-12px, 170px)" // 💻 iPad Pro → udesno 100px i dole 40px
            : "none",
          width: isSmallIpad
            ? "56px" // 📏 širina za manji iPad
            : isLargeIpad
            ? "80px" // 📏 širina za iPad Pro
            : "auto",

          willChange: "transform",
          // OVO JE KLJUČNA PROMJENA - pomjeramo slotove prema gore
          marginTop: getMarginTop(),
        }}
      >
        <SlotIndicator
          slotColor={color}
          isActive={isActive}
          sx={{
            animation: isWinning ? "winningPulse 0.6s ease-in-out 3" : "none",
            transformOrigin: "center",
          }}
        />

        <Typography
          variant="body1"
          sx={{
            color: color,
            fontSize: getFontSize(),
            fontWeight: 700,
            textShadow: isActive ? `0 0 8px ${color}` : "none",
            letterSpacing: "0.3px",
            fontFamily:
              "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            background: isActive
              ? `linear-gradient(45deg, ${color}, ${color}DD)`
              : "none",
            backgroundClip: isActive ? "text" : "initial",
            WebkitBackgroundClip: isActive ? "text" : "initial",
            WebkitTextFillColor: isActive ? "transparent" : "initial",
            animation: isActive ? "gentleGlow 2s ease-in-out infinite" : "none",
            transition: "all 0.3s ease",
            transform: "translateZ(0)",
            willChange: "transform",

            "@keyframes gentleGlow": {
              "0%, 100%": {
                opacity: 1000,
              },
              "50%": {
                opacity: 0.9,
              },
            },
          }}
        >
          {value}
        </Typography>

        {/* Optimized winning effect */}
        {isWinning && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) translateZ(0)",
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              background: `radial-gradient(circle at center, ${color}15 0%, transparent 70%)`,
              animation: "winningGlow 0.6s ease-out",
              zIndex: -1,
              willChange: "transform, opacity",

              "@keyframes winningGlow": {
                "0%": {
                  opacity: 0,
                  transform: "translate(-50%, -50%) scale(0.8) translateZ(0)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translate(-50%, -50%) scale(1) translateZ(0)",
                },
              },
            }}
          />
        )}
      </Stack>
    );
  }
);

MultiplierSlot.displayName = "MultiplierSlot";
